import _ from 'lodash';
import { formatAmountWithFraction } from '@riseupil/base-ui/src/utils/formatters';
import * as PlannerApi from '@/api/PlannerApi';

// Helper to strip extra fields from categories before sending to API
// Only keeps fields that the backend validation allows
// Note: Only include optional fields (changed, isNewItem) when they have meaningful values
const sanitizeCategoriesForApi = categories => {
  const sanitized = {};
  Object.keys(categories).forEach(category => {
    sanitized[category] = categories[category].map(item => {
      const result = {
        name: item.name,
        amount: item.amount,
        initialAmount: item.initialAmount,
      };
      // Only include optional fields when they have true values
      if (item.changed) {
        result.changed = true;
      }
      if (item.isNewItem) {
        result.isNewItem = true;
      }
      return result;
    });
  });
  return sanitized;
};

const state = {
  categories: {},
  initialCategories: {},
  // Saved scenarios state
  savedScenarios: [],
  currentScenarioId: null,
  scenariosLoading: false,
  scenariosError: null,
};

const mutations = {
  setCategories(state, categories) {
    state.categories = _.cloneDeep(categories);
  },
  setInitialCategories(state, categories) {
    state.initialCategories = _.cloneDeep(categories);
  },
  updateCategoryItem(state, { category, name, amount }) {
    const items = state.categories[category] || [];
    const idx = _.findIndex(items, e => e.name === name);
    if (idx !== -1 && formatAmountWithFraction(amount) !== formatAmountWithFraction(items[idx].amount)) {
      items[idx] = { ...items[idx], amount, changed: true };
      state.categories[category] = [...items];
    }
  },
  resetCategoryItem(state, { category, name }) {
    const items = state.categories[category] || [];
    const idx = _.findIndex(items, e => e.name === name);
    if (idx !== -1) {
      items[idx] = { ...items[idx], amount: items[idx].initialAmount, changed: false };
      state.categories[category] = [...items];
    }
  },
  addNewExpenseItem(state, { category, expense }) {
    const items = state.categories[category] || [];
    items.push(expense);
    state.categories[category] = [...items];
  },
  clearCustomExpenses(state) {
    state.categories.custom = [];
  },
  // Scenario mutations
  setSavedScenarios(state, scenarios) {
    state.savedScenarios = scenarios;
  },
  addSavedScenario(state, scenario) {
    state.savedScenarios = [scenario, ...state.savedScenarios];
  },
  updateSavedScenario(state, updatedScenario) {
    const idx = _.findIndex(state.savedScenarios, s => s.scenarioId === updatedScenario.scenarioId);
    if (idx !== -1) {
      state.savedScenarios = [
        ...state.savedScenarios.slice(0, idx),
        updatedScenario,
        ...state.savedScenarios.slice(idx + 1),
      ];
    }
  },
  removeSavedScenario(state, scenarioId) {
    state.savedScenarios = state.savedScenarios.filter(s => s.scenarioId !== scenarioId);
  },
  setCurrentScenarioId(state, scenarioId) {
    state.currentScenarioId = scenarioId;
  },
  setScenariosLoading(state, loading) {
    state.scenariosLoading = loading;
  },
  setScenariosError(state, error) {
    state.scenariosError = error;
  },
};

const actions = {
  initCategories({ commit }, categories) {
    const updatedCategories = {};

    categories.forEach(({ category, data }) => {
      updatedCategories[category] = data.map(e => ({
        ...e,
        changed: false,
        initialAmount: e.amount,
      }));
    });
    commit('setCategories', updatedCategories);
    commit('setInitialCategories', updatedCategories);
  },
  updateCategoryItemAmount({ commit }, { category, name, amount }) {
    commit('updateCategoryItem', { category, name, amount });
  },
  addNewExpense({ commit }, expense) {
    commit('addNewExpenseItem', {
      category: 'custom',
      expense: { ...expense, initialAmount: expense.amount, changed: false, isNewItem: true },
    });
  },
  resetCategoryItemChanged({ commit }, { category, name }) {
    commit('resetCategoryItem', { category, name });
  },
  resetAllCategories({ commit, state }) {
    commit('setCategories', state.initialCategories);
    commit('clearCustomExpenses');
    commit('setCurrentScenarioId', null);
  },

  // Scenario actions
  async loadSavedScenarios({ commit }) {
    commit('setScenariosLoading', true);
    commit('setScenariosError', null);
    try {
      const scenarios = await PlannerApi.getScenarios();
      commit('setSavedScenarios', scenarios);
    } catch (error) {
      commit('setScenariosError', error.message || 'Failed to load scenarios');
    } finally {
      commit('setScenariosLoading', false);
    }
  },

  async saveCurrentScenario({ commit, state }, { name }) {
    const scenario = await PlannerApi.createScenario({
      name,
      categories: sanitizeCategoriesForApi(state.categories),
    });
    commit('addSavedScenario', scenario);
    commit('setCurrentScenarioId', scenario.scenarioId);
    return scenario;
  },

  async updateCurrentScenario({ commit, state }) {
    if (!state.currentScenarioId) {
      throw new Error('No current scenario to update');
    }
    const updatedScenario = await PlannerApi.updateScenario(
      state.currentScenarioId,
      { categories: sanitizeCategoriesForApi(state.categories) },
    );
    commit('updateSavedScenario', updatedScenario);
    return updatedScenario;
  },

  async loadScenario({ commit }, scenarioId) {
    const scenario = await PlannerApi.getScenario(scenarioId);
    // Ensure loaded categories have proper initialAmount set
    const processedCategories = {};
    Object.keys(scenario.categories).forEach(category => {
      processedCategories[category] = scenario.categories[category].map(item => ({
        ...item,
        initialAmount: item.initialAmount ?? item.amount,
        changed: false,
      }));
    });
    commit('setCategories', processedCategories);
    commit('setCurrentScenarioId', scenario.scenarioId);
    return scenario;
  },

  async deleteScenario({ commit, state }, scenarioId) {
    await PlannerApi.deleteScenario(scenarioId);
    commit('removeSavedScenario', scenarioId);
    if (state.currentScenarioId === scenarioId) {
      commit('setCurrentScenarioId', null);
    }
  },

  // Helper to collect current plan data
  collectCurrentPlanData({ state }) {
    return {
      categories: _.cloneDeep(state.categories),
    };
  },
};

const getters = {
  getCategory: state => category => state.categories[category] || [],
  hasChangedItems: state => _.chain(state.categories)
    .values()
    .flatten()
    .some(e => e.changed)
    .value(),
  // Scenario getters
  getSavedScenarios: state => state.savedScenarios,
  getCurrentScenarioId: state => state.currentScenarioId,
  getCurrentScenario: state => {
    if (!state.currentScenarioId) {
      return null;
    }
    return state.savedScenarios.find(s => s.scenarioId === state.currentScenarioId);
  },
  isScenariosLoading: state => state.scenariosLoading,
  getScenariosError: state => state.scenariosError,
  hasUnsavedChanges: state => {
    if (!state.currentScenarioId) { return false; }
    const currentScenario = state.savedScenarios.find(s => s.scenarioId === state.currentScenarioId);
    if (!currentScenario) { return false; }
    return !_.isEqual(state.categories, currentScenario.categories);
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
